import { jsxs, jsx } from "react/jsx-runtime";
import { Box, Stack, Text, Bleed, ButtonIcon, IconClear, IconCritical } from "braid-design-system";
import { useMultipleSelection, useCombobox } from "downshift";
import { useRef, useId } from "react";
import { gap, flex, hidden, smallText, standardText, input, paddingY, noClearIconSpace, inputParentBorderColor, inputParent, suggestionList } from "./styles.css.mjs";
const mergeAccessibilityIds = (ids) => {
  const filteredIds = ids.filter(Boolean);
  if (filteredIds.length === 0) {
    return void 0;
  }
  return filteredIds.join(" ");
};
const MultiselectAutosuggest = ({
  id,
  size = "standard",
  label,
  message,
  tone = "neutral",
  suggestions,
  selectedItems,
  clearLabel,
  placeholder,
  searchText,
  hideLabel = false,
  dropdownBoxProps = {
    background: "surface",
    borderRadius: "standard",
    width: "full",
    zIndex: "dropdown",
    boxShadow: "medium",
    position: "absolute"
  },
  setSearchText,
  onClickSuggestionItem,
  onItemsSelected,
  renderSelectedItems,
  renderSuggestions,
  onMenuClose,
  isItemDisabled,
  onFocus,
  description
}) => {
  const inputRef = useRef(null);
  const descriptionId = useId();
  const messageId = useId();
  const { getSelectedItemProps, getDropdownProps } = useMultipleSelection({
    selectedItems,
    onStateChange: ({ type, selectedItems: newSelectedItems }) => {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          onItemsSelected(newSelectedItems ?? []);
          break;
      }
    }
  });
  const {
    isOpen,
    highlightedIndex,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    openMenu
  } = useCombobox({
    items: suggestions,
    itemToString: (item) => item ? item.id : "",
    inputValue: searchText,
    isItemDisabled: isItemDisabled ?? (() => false),
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          const newChanges = {
            ...changes,
            isOpen: true,
            // keeps the menu open after selection.
            highlightedIndex: state.highlightedIndex
          };
          return newChanges;
        case useCombobox.stateChangeTypes.InputClick:
          return {
            ...changes,
            isOpen: true
          };
        default:
          return changes;
      }
    },
    onStateChange({ inputValue: newInputValue, type, selectedItem }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          if (onMenuClose) {
            onMenuClose();
          }
          if (selectedItem) {
            onClickSuggestionItem(selectedItem);
          }
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          if (onMenuClose) {
            onMenuClose();
          }
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            onClickSuggestionItem(selectedItem);
          }
          break;
        case useCombobox.stateChangeTypes.InputChange:
          setSearchText(newInputValue ?? "");
          break;
      }
    }
  });
  const hasSelectedItems = selectedItems.length > 0;
  const shouldShowClearIcon = searchText.length > 0;
  return /* @__PURE__ */ jsxs(
    Box,
    {
      id: `${id}-multiselect-autosuggest`,
      display: "flex",
      flexDirection: "column",
      position: "relative",
      width: "full",
      className: gap[size],
      children: [
        /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsx(
            Box,
            {
              display: hideLabel ? "none" : "block",
              component: "label",
              ...getLabelProps({
                onClick: () => {
                  openMenu();
                }
              }),
              children: /* @__PURE__ */ jsx(Text, { size, weight: "strong", children: label })
            }
          ),
          description ? /* @__PURE__ */ jsx(Text, { tone: "secondary", id: descriptionId, size, children: description }) : null
        ] }),
        /* @__PURE__ */ jsxs(Box, { children: [
          /* @__PURE__ */ jsxs(
            Box,
            {
              position: "relative",
              background: "surface",
              width: "full",
              onClick: (e) => {
                openMenu();
                e.stopPropagation();
              },
              className: {
                [inputParent]: true,
                [inputParentBorderColor[tone]]: true,
                [noClearIconSpace]: shouldShowClearIcon,
                [paddingY[size]]: true
              },
              children: [
                renderSelectedItems ? renderSelectedItems({ getSelectedItemProps }) : null,
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    className: `${isOpen || searchText || !searchText && selectedItems.length === 0 ? flex : hidden} ${size === "small" ? smallText : standardText} ${input}`,
                    placeholder: hasSelectedItems ? "" : placeholder,
                    "aria-describedby": mergeAccessibilityIds([
                      description ? descriptionId : void 0,
                      message ? messageId : void 0
                    ]),
                    onFocus,
                    ...getInputProps(
                      getDropdownProps({ ref: inputRef, value: searchText }),
                      {
                        suppressRefError: true
                      }
                    )
                  }
                ),
                shouldShowClearIcon ? /* @__PURE__ */ jsx(
                  Box,
                  {
                    component: "span",
                    position: "absolute",
                    width: "touchable",
                    height: "touchable",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    top: 0,
                    right: 0,
                    children: /* @__PURE__ */ jsx(Bleed, { vertical: "xxsmall", children: /* @__PURE__ */ jsx(
                      ButtonIcon,
                      {
                        id: `${id}-clear-multiselect-autosuggest-input`,
                        label: clearLabel || "Clear",
                        variant: "transparent",
                        tone: "neutral",
                        type: "reset",
                        onClick: () => setSearchText(""),
                        icon: /* @__PURE__ */ jsx(IconClear, {})
                      }
                    ) })
                  }
                ) : null
              ]
            }
          ),
          /* @__PURE__ */ jsx(Box, { position: "relative", children: /* @__PURE__ */ jsx(
            Box,
            {
              component: "ul",
              position: "relative",
              display: isOpen ? "block" : "none",
              ...dropdownBoxProps,
              className: suggestionList,
              ...getMenuProps({}, { suppressRefError: true }),
              children: renderSuggestions({ highlightedIndex, getItemProps })
            }
          ) }),
          message ? /* @__PURE__ */ jsx(Box, { paddingTop: "xsmall", position: "relative", children: /* @__PURE__ */ jsx(
            Text,
            {
              id: messageId,
              tone: tone === "neutral" ? "secondary" : tone,
              icon: tone === "critical" ? /* @__PURE__ */ jsx(IconCritical, { tone: "critical" }) : null,
              size: "small",
              children: message
            }
          ) }) : null
        ] })
      ]
    }
  );
};
export {
  MultiselectAutosuggest
};
